import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus } from "lucide-react";
import { FRAGRANCES, FRAGRANCE_GALLERY } from "../data/content";
import { useCart } from "../context/CartContext";
import { Reveal } from "./Reveal";

const NoteRow = ({ label, items }) => (
  <div className="grid grid-cols-[70px_1fr] gap-4 items-baseline py-3 border-b border-bds-border">
    <span className="font-mono-bds text-[10px] uppercase tracking-[0.25em] text-bds-accent">{label}</span>
    <span className="font-serif-display text-lg md:text-xl text-bds-fg/90">{items.join(", ")}</span>
  </div>
);

const FragranceDetail = ({ f, onClose }) => {
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const media = [{ type: "video", src: f.video, poster: f.img }, { type: "img", src: f.img },
    ...FRAGRANCE_GALLERY.map((g) => ({ type: "img", src: g.img }))];
  const [active, setActive] = useState(0);
  const cur = media[active];

  const add = () => {
    addItem({
      lookup_key: f.lookup_key,
      name: `${f.name} — EDP`,
      price: f.price,
      wash: "Eau de Parfum",
      size: "50ml",
      designImg: f.img,
      quantity: qty,
    });
    onClose();
  };

  return (
    <motion.div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-bds-bg/90 backdrop-blur-md" onClick={onClose} />
      <motion.div
        data-testid="fragrance-detail"
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.5 }}
        className="relative w-full max-w-5xl bg-bds-surface border border-bds-border max-h-[92vh] overflow-y-auto grid grid-cols-1 md:grid-cols-2"
      >
        <button onClick={onClose} data-testid="fragrance-detail-close"
          className="absolute top-4 right-4 z-10 w-10 h-10 grid place-items-center border border-bds-border bg-bds-bg/50 hover:bg-bds-fg hover:text-bds-bg transition-colors">
          <X size={16} />
        </button>

        <div className="bg-bds-bg p-4 md:p-6 border-r border-bds-border">
          <div className="relative overflow-hidden aspect-square bg-black flex items-center justify-center">
            {cur.type === "video" ? (
              <video key={cur.src} data-testid="fragrance-video" className="w-full h-full object-cover"
                src={cur.src} poster={cur.poster} autoPlay muted loop playsInline />
            ) : (
              <img key={cur.src} src={cur.src} alt={f.name} className="w-full h-full object-contain animate-[fadeIn_0.4s_ease]" />
            )}
          </div>
          <div className="grid grid-cols-5 gap-2 mt-3">
            {media.map((m, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`frag-media-${i}`}
                className={`relative overflow-hidden h-16 border-2 transition-colors ${active === i ? "border-bds-accent" : "border-transparent hover:border-bds-muted"}`}>
                <img src={m.type === "video" ? m.poster : m.src} alt="" className="w-full h-full object-cover" />
                {m.type === "video" && (
                  <span className="absolute inset-0 grid place-items-center bg-black/40 text-bds-cream text-[9px] font-mono-bds uppercase tracking-widest">Film</span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 md:p-10">
          <p className="font-mono-bds text-[11px] uppercase tracking-[0.25em] text-bds-accent mb-2">{f.roman} — Eau de Parfum</p>
          <h3 className="font-serif-display text-4xl md:text-5xl mb-4">{f.name}</h3>
          <p className="font-grotesk text-bds-muted leading-relaxed mb-8">{f.story}</p>

          <div className="mb-8">
            <p className="font-mono-bds text-[11px] uppercase tracking-[0.25em] text-bds-muted mb-2">Olfactive Pyramid</p>
            <NoteRow label="Top" items={f.top} />
            <NoteRow label="Heart" items={f.mid} />
            <NoteRow label="Base" items={f.base} />
          </div>

          <p className="font-serif-display text-3xl mb-6">NZ${f.price}</p>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center border border-bds-border">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-12 grid place-items-center hover:bg-bds-fg hover:text-bds-bg transition-colors" data-testid="frag-qty-minus"><Minus size={14} /></button>
              <span className="w-10 text-center font-mono-bds" data-testid="frag-qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="w-11 h-12 grid place-items-center hover:bg-bds-fg hover:text-bds-bg transition-colors" data-testid="frag-qty-plus"><Plus size={14} /></button>
            </div>
            <button onClick={add} data-testid="frag-add-to-cart-btn"
              className="flex-1 font-mono-bds text-xs uppercase tracking-[0.2em] h-12 bg-bds-fg text-bds-bg hover:bg-bds-accent hover:text-bds-cream transition-colors">
              Add — NZ${f.price * qty}
            </button>
          </div>
          <p className="font-mono-bds text-[11px] uppercase tracking-[0.12em] text-bds-muted leading-relaxed border-t border-bds-border pt-6">
            50ml · Extrait strength · Numbered edition of 500 · rigid magnetic box, wax-sealed authenticity card
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Fragrances = () => {
  const { addItem } = useCart();
  const [active, setActive] = useState(null);

  const quickAdd = (e, f) => {
    e.stopPropagation();
    addItem({ lookup_key: f.lookup_key, name: `${f.name} — EDP`, price: f.price, wash: "Eau de Parfum", size: "50ml", designImg: f.img, quantity: 1 });
  };

  return (
    <section id="scents" data-testid="fragrances" className="relative py-28 md:py-44 border-t border-bds-border">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
            <div>
              <span className="font-mono-bds text-xs uppercase tracking-[0.3em] text-bds-accent">Eau de Parfum</span>
              <h2 className="font-serif-display font-light text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] mt-6">
                Four scents, <span className="italic">four stories</span>
              </h2>
            </div>
            <p className="font-grotesk text-bds-muted max-w-sm leading-relaxed">
              Every mind carries a shadow. Tap any bottle for the full ritual — film, packaging, and its olfactive pyramid. Numbered editions of 500. 50ml · NZD.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {FRAGRANCES.map((f, i) => (
            <Reveal key={f.lookup_key} delay={i * 0.06}>
              <div data-testid={`fragrance-${f.lookup_key}`} className="group flex flex-col h-full">
                <button onClick={() => setActive(f)} data-cursor="art" data-testid={`view-fragrance-${f.lookup_key}`}
                  className="relative bg-bds-surface border border-bds-border overflow-hidden text-left">
                  <img src={f.img} alt={f.name} className="w-full h-72 object-cover object-center transition-transform duration-700 group-hover:scale-105" />
                  <span className="absolute top-3 left-3 font-serif-display text-2xl text-bds-fg/70">{f.roman}</span>
                  <span className="absolute bottom-3 right-3 font-mono-bds text-[9px] uppercase tracking-[0.2em] text-bds-cream bg-bds-bg/60 px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">View ritual</span>
                </button>
                <div className="pt-5 flex-1 flex flex-col">
                  <h3 className="font-serif-display text-2xl">{f.name}</h3>
                  <p className="font-serif-display italic text-sm text-bds-muted mt-1 mb-3">{f.story.split("—")[0]}</p>
                  <p className="font-mono-bds text-[10px] uppercase tracking-[0.12em] text-bds-muted leading-relaxed">{f.notes}</p>
                  <div className="mt-auto pt-5 flex items-center justify-between gap-3">
                    <span className="font-serif-display text-xl">NZ${f.price}</span>
                    <button onClick={(e) => quickAdd(e, f)} data-testid={`add-fragrance-${f.lookup_key}`}
                      className="font-mono-bds text-[11px] uppercase tracking-[0.2em] px-5 py-3 border border-bds-fg hover:bg-bds-fg hover:text-bds-bg transition-colors">
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && <FragranceDetail f={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
};
